import React, { useMemo } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import type { HomeTestimonial } from './homeTypes';

/** Client fallback when API/table is empty (migration not applied yet). */
const FALLBACK_TESTIMONIALS: HomeTestimonial[] = [
  {
    id: -1,
    quote:
      'Finally a place where the mod list and the photos stay with the car. I shared one link at the meet and people actually got the full story.',
    author_name: 'Kai Mendoza',
    vehicle_label: "'19 GR Supra",
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -2,
    quote:
      'Imported my messy parts spreadsheet in one pass. Cleaning it up inside the build felt better than rewriting Discord messages.',
    author_name: 'Marina Cho',
    vehicle_label: "'90 Miata",
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -3,
    quote:
      'Awards from our local show showed up on the profile the same night. That is the whole reason I switched.',
    author_name: 'Nate Alvarez',
    vehicle_label: "'15 WRX STI",
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -4,
    quote:
      'The QR on the windshield is the first thing judges ask for now. No more hunting for a Google Drive folder.',
    author_name: 'Priya Shah',
    vehicle_label: "'18 Civic Type R",
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -5,
    quote:
      'Adding the missing photos and specs made the build page feel finished. Visitors stopped asking for details I had already written down.',
    author_name: 'Owen Briggs',
    vehicle_label: "'14 GT-R",
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -6,
    quote:
      'Club badges on the car page beat another private Facebook album. New members find us through the builds.',
    author_name: 'Lena Ortiz',
    vehicle_label: 'Air-cooled 911',
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -7,
    quote:
      'I can send shops the same profile I use for events. Specs, invoices, and progress photos in one place.',
    author_name: 'Chris Vu',
    vehicle_label: "'07 S2000",
    rating: 5,
    is_placeholder: true,
  },
  {
    id: -8,
    quote:
      'Not a feed. Not a caption contest. Just the record of what we did to the car, in order.',
    author_name: 'Sam Rivera',
    vehicle_label: "'22 BRZ",
    rating: 5,
    is_placeholder: true,
  },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

function TestimonialCard({ item }: { item: HomeTestimonial }) {
  const stars = Math.max(1, Math.min(5, item.rating ?? 5));
  return (
    <figure className="w-[min(20rem,78vw)] shrink-0 rounded-2xl border border-white/[0.08] bg-[#111] p-5 flex flex-col gap-4">
      <div className="flex items-center gap-0.5" aria-label={`${stars} out of 5 stars`}>
        {Array.from({ length: stars }, (_, i) => (
          <StarIcon key={i} className="h-4 w-4 text-orange-400" aria-hidden />
        ))}
      </div>
      <blockquote className="text-gray-200 text-sm leading-relaxed flex-1">
        <p className="line-clamp-3">&ldquo;{item.quote}&rdquo;</p>
      </blockquote>
      <figcaption className="flex items-center gap-3 pt-1 border-t border-white/[0.06]">
        {item.avatar_url ? (
          <img
            src={item.avatar_url}
            alt=""
            className="h-9 w-9 rounded-full object-cover bg-[#0a0a0a]"
            loading="lazy"
          />
        ) : (
          <span
            className="h-9 w-9 rounded-full bg-orange-500/15 border border-orange-500/25 text-orange-300 text-xs font-semibold flex items-center justify-center shrink-0"
            aria-hidden
          >
            {initials(item.author_name)}
          </span>
        )}
        <div className="min-w-0">
          <p className="text-white text-sm font-medium truncate">{item.author_name}</p>
          <p className="text-gray-500 text-xs truncate">
            {[
              item.vehicle_label,
              !item.is_placeholder && item.author_handle
                ? `@${item.author_handle.replace(/^@/, '')}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </figcaption>
    </figure>
  );
}

function MarqueeRow({
  items,
  direction,
  durationSec,
}: {
  items: HomeTestimonial[];
  direction: 'left' | 'right';
  durationSec: number;
}) {
  const loop = useMemo(() => [...items, ...items], [items]);
  const anim =
    direction === 'left' ? 'animate-proof-marquee-left' : 'animate-proof-marquee-right';

  return (
    <div className="relative overflow-hidden motion-reduce:overflow-visible">
      <div
        className={`flex w-max gap-3 sm:gap-4 motion-reduce:hidden ${anim}`}
        style={{ animationDuration: `${durationSec}s` }}
      >
        {loop.map((item, idx) => (
          <TestimonialCard key={`${item.id}-${idx}`} item={item} />
        ))}
      </div>
      <div className="hidden motion-reduce:flex flex-wrap justify-center gap-3 sm:gap-4 px-4">
        {items.map((item) => (
          <TestimonialCard key={`static-${item.id}`} item={item} />
        ))}
      </div>
    </div>
  );
}

export interface HomeSocialProofProps {
  testimonials?: HomeTestimonial[] | null;
}

/**
 * Scrolling social proof for the public home.
 * Pink Slips–cued dual-row marquee; Bilt copy and orange accent.
 * Content is admin-managed (`home_testimonials`); placeholders until real quotes are sourced.
 */
export const HomeSocialProof: React.FC<HomeSocialProofProps> = ({ testimonials }) => {
  const items = useMemo(() => {
    if (testimonials && testimonials.length > 0) return testimonials;
    return FALLBACK_TESTIMONIALS;
  }, [testimonials]);

  const mid = Math.ceil(items.length / 2);
  const rowA = items.slice(0, mid);
  const rowB = items.slice(mid);
  const rowBSafe = rowB.length > 0 ? rowB : items;

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden border-y border-white/[0.05] bg-[#0c0c0c]"
      aria-labelledby="social-proof-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 sm:mb-12 text-center">
        <h2
          id="social-proof-heading"
          className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white text-balance"
        >
          Builders already keeping the record
        </h2>
        <p className="text-gray-400 text-base sm:text-lg mt-4 leading-relaxed max-w-[42ch] mx-auto">
          Short notes from people documenting real cars. More will land here as the community grows.
        </p>
      </div>

      <div className="space-y-3 sm:space-y-4" aria-label="Builder testimonials">
        <MarqueeRow items={rowA} direction="left" durationSec={48} />
        <MarqueeRow items={rowBSafe} direction="right" durationSec={56} />
      </div>

      {/* Static list for screen readers / reduced-motion preference handled via CSS pause */}
      <ul className="sr-only">
        {items.map((item) => (
          <li key={item.id}>
            {item.author_name}: {item.quote}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default HomeSocialProof;
